import { useMatch, useNavigate } from 'react-router-dom';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import ListPane from '@/views/list/ListPane';
import {
  Flex,
  Box,
  Group,
  Loader,
  Menu,
  Stack,
  useMantineTheme,
  Alert,
  UnstyledButton,
} from '@mantine/core';
import {
  ChevronLeft,
  Info,
  Link,
  MoreVertical,
  Trash,
  AlertCircle,
} from 'react-feather';
import { IconButton } from '@/components';

import {
  LISTS_BASE_PATH,
  LISTS_DETAIL_PATH,
  LISTS_SPAN_PATH,
} from '@/app/constants';
import {
  useDeleteListMutation,
  useGetListByIdQuery,
  useUpdateListMutation,
  useBulkRemoveFromListMutation,
} from '@/features/customListSlice';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useDisclosure } from '@mantine/hooks';
import { useClientTerminology } from '@/views/list/NetworkTerms';

import classes from './CustomList.module.css';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ListDetailsForm from './ListDetailsForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { ListItems } from '@/views/list/ListItems';
import {
  type Span,
  type GroupAnalysisFilter,
} from '@/generated/types/navigator/types.gen';

import { captureException } from '@sentry/react';
import { useConfirm } from '@/components/Modals/useConfirm';
import { ProjectExport } from '@/views/projects/ProjectExport';
import {
  hasMoreToLoad as hasMoreToLoadLogic,
  mergeSpanItems,
  transformSpansToAnalyses,
} from './utils';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useListHeight } from '@/hooks/useListHeight';
import { useScrolled } from '@/hooks/useScrolled';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import CustomListEmptyMessage from '@/views/list/custom-lists/CustomListEmptyMessage';
import { AddSpansToProjectFilter } from './AddSpansToProjectFilter';
import {
  encodeFilterQuery,
  getFilterQuery,
  generateBase64FilterQuery,
} from '@/features/filterHelpers';
import { clearQuery } from '@/features/filterSlice';
import { ClearFilter } from '@/views/list/ClearFilter';
import { ObjectiveDropdown } from '@/views/list/analysisList/ObjectiveDropdown';
import { BulkActionDropdown } from '@/views/list/BulkActionDropdown';
import {
  openWorklogModal,
  WorklogModalMode,
} from '@/features/worklogModalSlice';
import { ListSummary } from '@/views/list/ListSummary';
import { MapListToggle } from '@/views/list/MapListToggle';
import { incrementMvtCacheCounter } from '@/features/analysisMapSlice';

/**
 * Create a stable query key to store results in
 */
const createQueryKey = (
  id: string | undefined,
  selectedAnalysis: string,
  filterQuery: GroupAnalysisFilter
): string => {
  return `${id}-${selectedAnalysis}-${encodeFilterQuery(filterQuery)}`;
};

export default function CustomListDetailPane() {
  const { selectedAnalysis, isListView } = useAppSelector(
    (state) => state.analysisMap
  );
  const layerConfig = useAppSelector((s) => s.mapLayer);
  const query = useAppSelector((state) => state.filterQuery);
  const [bulkRemoveFromList] = useBulkRemoveFromListMutation();

  /**
   * We use this filter for filtering _within_ a project
   *
   * TODO: Consider utilizing a local filter state instead of global to avoid side effects
   **/
  const globalFilterState = useAppSelector((state) => state.filterQuery);

  // Convert filter state to API format and memoize it
  const filterQuery = useMemo(() => {
    return getFilterQuery(globalFilterState);
  }, [globalFilterState]);

  const listMatch = useMatch(LISTS_DETAIL_PATH);
  const spanMatch = useMatch(LISTS_SPAN_PATH);
  const dispatch = useAppDispatch();

  // Get ID from either route pattern
  const { id } = listMatch?.params || spanMatch?.params || {};

  const navigate = useNavigate();
  const navigateWithSearch = useNavigateWithSearch();
  const theme = useMantineTheme();
  const intl = useClientTerminology();

  // State for controlling the AddSpansToProjectFilter
  const [
    isAddSpansFilterOpen,
    { open: openAddSpansFilter, close: closeAddSpansFilter },
  ] = useDisclosure(false);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setScrollAreaRef] =
    useScrolled<HTMLDivElement>(scrollAreaRef);
  const isSmallScreen = useSmallScreen();

  // Create a stable key that represents the current query identity
  const queryIdentity = useMemo(() => {
    return createQueryKey(id, selectedAnalysis, filterQuery);
  }, [id, selectedAnalysis, filterQuery]);

  // Items stored by their query key
  const [itemsByQuery, setItemsByQuery] = useState<Record<string, Span[]>>({});
  const [page, setPage] = useState(1);
  const confirm = useConfirm();

  // Current items are derived from the query parameters
  const currentItems = useMemo(() => {
    return itemsByQuery[queryIdentity] || [];
  }, [itemsByQuery, queryIdentity]);

  // Memoize these, as not doing so causes a lot of re-fetches as the app stabilizes on initial load.
  const queryParams = useMemo(() => {
    if (!id) return skipToken;
    return {
      id,
      params: {
        analysisType: selectedAnalysis,
        page,
        filterQuery,
      },
    };
  }, [id, selectedAnalysis, page, filterQuery]);

  const {
    data: response,
    isError,
    isLoading,
    isFetching,
  } = useGetListByIdQuery(queryParams);

  const summary = response?.summary;
  const isLoadingOrFetching = isFetching || isLoading;

  useDocumentTitle({
    title: summary?.name || 'Project Detail',
    paths: [LISTS_DETAIL_PATH],
  });

  const [deleteMutation] = useDeleteListMutation();

  const [showExportModal, { open: openExportModal, close: closeExportModal }] =
    useDisclosure(false);

  const [
    editListModalOpened,
    { open: openEditListModal, close: closeEditListModal },
  ] = useDisclosure(false);
  const [editMutation, { isLoading: isEditing }] = useUpdateListMutation();

  const resetItems = useCallback(() => {
    setItemsByQuery({});
    setPage(1);
    scrollAreaRef.current?.scrollTo({ top: 0 });
  }, []);

  // Reset items when the query identity changes (id, analysis type, or filter)
  useEffect(() => {
    resetItems();
  }, [queryIdentity, resetItems]);

  useEffect(() => {
    const spans = response?.data;
    if (!isFetching && spans) {
      setItemsByQuery((prev) => {
        const currentForQuery = prev[queryIdentity] || [];
        const merged = mergeSpanItems(currentForQuery, spans, page === 1);

        return {
          ...prev,
          [queryIdentity]: merged,
        };
      });
    }
  }, [response, page, isFetching, queryIdentity]);

  const handleDelete = useCallback(async () => {
    if (!id) return;

    try {
      const results = await deleteMutation(id);

      if ('error' in results) {
        throw new Error();
      }

      notifications.show({
        title: intl.formatMessage({
          id: 'action.success',
        }),
        message: intl.formatMessage(
          {
            id: 'action.deleteSuccessMessage',
          },
          { item: summary?.name || '' }
        ),
        color: 'green',
      });

      navigate(LISTS_BASE_PATH);
    } catch (error) {
      captureException(error);
      notifications.show({
        title: intl.formatMessage({
          id: 'customList.deleteError',
        }),
        message: intl.formatMessage({
          id: 'customList.deleteErrorMessage',
        }),
        color: 'red',
      });
    }
  }, [deleteMutation, id, intl, summary?.name, navigate]);

  const handleEditListSubmit = useCallback(
    async (values: { name: string; description?: string }) => {
      if (!id) return;

      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.projects.managing.edit,
        },
      });

      try {
        await editMutation({
          listId: id,
          body: {
            name: values.name,
            description: values.description || '',
          },
        });
      } catch (error) {
        captureException(error);
        notifications.show({
          title: intl.formatMessage({
            id: 'customList.editError',
          }),
          message: intl.formatMessage({
            id: 'customList.editErrorMessage',
          }),
          color: 'red',
        });
      }
      closeEditListModal();
    },
    [closeEditListModal, editMutation, id, intl]
  );

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard.writeText(window.location.href);
    notifications.show({
      title: intl.formatMessage({
        id: 'action.success',
      }),
      message: intl.formatMessage({
        id: 'action.copyLinkSuccess',
      }),
      color: 'green',
    });
  }, [intl]);

  const hasMoreToLoad = useCallback(() => {
    return hasMoreToLoadLogic(summary?.total, currentItems.length);
  }, [currentItems.length, summary?.total]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoadingOrFetching) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreToLoad()) {
          setPage((prevPage: number) => prevPage + 1);
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    [isLoadingOrFetching, hasMoreToLoad, setPage]
  );

  const currentAnalyses = useMemo(() => {
    return transformSpansToAnalyses(currentItems);
  }, [currentItems]);

  const handleRequestDeleteList = useCallback(async () => {
    const result = await confirm({
      titleId: 'customList.deleteList',
      contentId: 'customList.deleteConfirmationMessage',
      primaryLabelId: 'customList.deleteList',
      danger: true,
    });

    if (result.confirmed) {
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.projects.managing.delete,
        },
      });

      handleDelete();
    }
  }, [confirm, handleDelete]);

  const handleRemoveFromList = useCallback(async () => {
    if (!id) return;

    try {
      const result = await bulkRemoveFromList({
        listId: id,
        body: {
          removal_filter: generateBase64FilterQuery(query),
        },
      }).unwrap();

      dispatch(clearQuery());
      dispatch(incrementMvtCacheCounter());

      notifications.show({
        title: intl.formatMessage({ id: 'action.success' }),
        message: intl.formatMessage(
          {
            id: 'customList.removeFilteredFromListSuccess',
          },
          {
            count: Math.abs(result.summary.modified || 0),
            projectName: summary?.name || '',
          }
        ),
        color: 'green',
      });
    } catch (error) {
      captureException(error);
      notifications.show({
        title: intl.formatMessage({ id: 'action.error' }),
        message: intl.formatMessage({
          id: 'customList.removeFilteredFromListError',
        }),
        color: 'red',
      });
    }
  }, [bulkRemoveFromList, id, query, dispatch, intl, summary?.name]);

  const handleRequestRemoveFromList = useCallback(async () => {
    const result = await confirm({
      titleId: 'customList.removeFromProjectConfirmationTitle',
      contentId: 'customList.removeFromProjectConfirmationMessage',
      contentVariables: { count: summary?.total || 0 },
      primaryLabelId: 'customList.removeFromProjectConfirmationButton',
      danger: true,
    });

    if (result.confirmed) {
      handleRemoveFromList();
    }
  }, [confirm, handleRemoveFromList, summary?.total]);

  const listHeight = useListHeight({
    isSmallScreen,
    isListView,
  });

  const isActuallyEmptyProject = useMemo(
    () =>
      !isLoadingOrFetching &&
      currentAnalyses?.length === 0 &&
      !isError &&
      !globalFilterState.isSelected,

    [
      isLoadingOrFetching,
      currentAnalyses?.length,
      isError,
      globalFilterState.isSelected,
    ]
  );

  const isFilteredToEmpty = useMemo(
    () =>
      !isLoadingOrFetching &&
      !isActuallyEmptyProject &&
      currentAnalyses?.length === 0 &&
      !isError,
    [
      currentAnalyses?.length,
      isActuallyEmptyProject,
      isError,
      isLoadingOrFetching,
    ]
  );

  return (
    <>
      <ListPane
        className={`${classes.root} ${isListView ? classes.listViewMode : ''}`}
        listStyles={{
          height: listHeight,
        }}
        listWrapperStyles={{
          // If the screen is small and the list view is active, override the zIndex
          // that's set in the CSS Module
          zIndex: isSmallScreen && isListView ? 1 : undefined,
          overflow: 'visible',
        }}
        isLoaded={!isLoadingOrFetching}
      >
        <Box className={classes.header} data-scrolled={isScrolled || undefined}>
          <Box px="md" pb="xs">
            {/* Nav row: < Back ··· ⋮ */}
            <Flex
              align="center"
              justify="space-between"
              className={classes.navRow}
            >
              <UnstyledButton
                onClick={() => navigateWithSearch('/')}
                className={classes.backButton}
              >
                <ChevronLeft size={16} />
                {intl.formatMessage({ id: 'action.back' })}
              </UnstyledButton>
              <Flex align="center" gap={8}>
                {isSmallScreen && (
                  <ObjectiveDropdown
                    layerConfig={layerConfig}
                    showGroupBy={false}
                    compact
                  />
                )}
                <Menu
                  shadow="md"
                  width={200}
                  position="bottom-end"
                  offset={-10}
                >
                  <Menu.Target>
                    <IconButton
                      Component={MoreVertical}
                      data-testid="detail-view-header-menu"
                    />
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={openEditListModal}
                      leftSection={
                        <Info
                          size={14.5}
                          color={theme.other.mainColor.accent.normal}
                        />
                      }
                      color={theme.other.mainColor.accent.normal}
                      data-testid="detail-view-header-menu-item-edit"
                    >
                      {intl.formatMessage({ id: 'customList.listDetails' })}
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        handleCopyLink();
                        TagManager.dataLayer({
                          dataLayer: {
                            event: GoogleEvents.projects.engagement.copyUrl,
                          },
                        });
                      }}
                      leftSection={
                        <Link
                          size={14.5}
                          color={theme.other.mainColor.accent.normal}
                        />
                      }
                      color={theme.other.mainColor.accent.normal}
                      data-testid="detail-view-header-menu-item-copy-link"
                    >
                      {intl.formatMessage({ id: 'action.copyLink' })}
                    </Menu.Item>
                    <Menu.Item
                      onClick={handleRequestDeleteList}
                      leftSection={
                        <Trash size={14.5} color={theme.colors.red[6]} />
                      }
                      color={theme.colors.red[6]}
                      data-testid="detail-view-header-menu-item-delete"
                    >
                      {intl.formatMessage({ id: 'customList.deleteList' })}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Flex>
            </Flex>

            {/* Title row */}
            <Box mt={4}>
              <div
                style={theme.other.typography.variants.h4}
                className={classes.heading}
                data-testid="detail-view-header-heading"
              >
                {summary?.name ||
                  (isError && intl.formatMessage({ id: 'action.error' })) ||
                  (isLoading && intl.formatMessage({ id: 'action.loading' }))}
              </div>
            </Box>

            <Stack gap="xxs">
              <ListSummary
                unitCount={summary?.total || 0}
                distance={summary?.length || 0}
                label={intl.formatMessage({
                  id: 'customList.project',
                  defaultMessage: 'Project',
                })}
                infoTooltip={intl.formatMessage({
                  id: 'customList.DataMessage',
                })}
                isLoading={isLoadingOrFetching}
              />
              <Group justify="space-between" data-testid="map-list-toggle-row">
                <Box>
                  {!isLoadingOrFetching && (
                    <BulkActionDropdown
                      onSpanExport={openExportModal}
                      onRemoveFromList={handleRequestRemoveFromList}
                      onAddActivity={() =>
                        dispatch(
                          openWorklogModal({
                            mode: WorklogModalMode.BULK_ADD,
                            filter: globalFilterState,
                            spanCount: summary?.total ?? undefined,
                          })
                        )
                      }
                    />
                  )}
                </Box>
                {isSmallScreen && <MapListToggle />}
              </Group>
            </Stack>
          </Box>
        </Box>

        {isListView && (
          <>
            {isLoadingOrFetching && (
              <Flex justify="center" mt="md">
                <Loader size="sm" />
              </Flex>
            )}
            {!isLoadingOrFetching && isError && (
              <Alert
                variant="light"
                color="redBrand"
                title={intl.formatMessage({ id: 'action.error' })}
                icon={<AlertCircle />}
              >
                {intl.formatMessage({ id: 'customList.loadError' })}
              </Alert>
            )}
            {isActuallyEmptyProject && (
              <CustomListEmptyMessage openAddSpansFilter={openAddSpansFilter} />
            )}
            {isFilteredToEmpty && <ClearFilter p="md" />}
            {currentAnalyses?.length > 0 && (
              <div
                className={`${classes.scrollContainer} ${classes.customListDetailScrollContainer}`}
                data-testid="custom-list-scroll-area"
                ref={setScrollAreaRef}
                style={{
                  transition: 'box-shadow 0.2s ease',
                  boxShadow: isScrolled
                    ? 'inset 0 8px 8px -8px rgba(0, 0, 0, 0.12)'
                    : undefined,
                }}
              >
                <ListItems items={currentAnalyses} lastItemRef={lastItemRef} />
              </div>
            )}
          </>
        )}
      </ListPane>

      <ListDetailsForm
        key={summary?.id} // Ensure the form re-renders when the list changes
        opened={editListModalOpened}
        onClose={() => {
          TagManager.dataLayer({
            dataLayer: {
              event: GoogleEvents.projects.clickOut.projectDetails,
            },
          });
          closeEditListModal();
        }}
        onSubmit={handleEditListSubmit}
        title={intl.formatMessage({
          id: 'customList.editList',
        })}
        submitButtonText={intl.formatMessage({
          id: 'action.save',
        })}
        initialValues={{
          name: summary?.name || '',
          description: summary?.description || '',
        }}
        isSaving={isEditing}
      />

      {id ? (
        <ProjectExport
          showExportModal={showExportModal}
          onClose={closeExportModal}
          projectId={id}
          selectedAnalysis={selectedAnalysis}
        />
      ) : null}

      {id && !isError ? (
        <AddSpansToProjectFilter
          projectId={id}
          isLoading={isLoading || isFetching}
          totalSpans={summary?.total || 0}
          onSpansAdded={resetItems}
          isOpen={isAddSpansFilterOpen && isActuallyEmptyProject}
          requestOpen={openAddSpansFilter}
          requestClose={closeAddSpansFilter}
        />
      ) : null}
    </>
  );
}
